<template>
  <!-- ローディング中 -->
  <div v-if="loading" class="weather-loading">
    <span class="weather-loading-dot"></span>
    <span class="weather-loading-dot"></span>
    <span class="weather-loading-dot"></span>
  </div>
  <!-- 16日より先 -->
  <div v-else-if="isFarFuture && mode === 'strip'" class="weather-far-future">
    ☁️ 日程が先なのでまだ天気予報を表示できません
  </div>
  <template v-else-if="!hasError">
    <!-- ストリップモード: 複数日 -->
    <div v-if="mode === 'strip' && days.length > 0" class="weather-strip">
      <div v-for="day in days" :key="day.date" class="weather-day">
        <span class="wd-date">{{ formatShortDate(day.date) }}</span>
        <span class="wd-icon">{{ day.icon }}</span>
        <span class="wd-temp"
          >{{ Math.round(day.max) }}°<span class="wd-sep">/</span
          >{{ Math.round(day.min) }}°</span
        >
      </div>
    </div>
    <!-- バッジモード: 1日 -->
    <span v-else-if="mode === 'badge' && singleDay" class="weather-badge">
      {{ singleDay.icon }} {{ Math.round(singleDay.max) }}°/{{
        Math.round(singleDay.min)
      }}°
    </span>
  </template>
</template>

<script>
import { ref, computed, watch } from "vue";

// モジュールレベルのキャッシュ（コンポーネント間で共有・重複リクエスト防止）
const _geoCache = new Map();
const _geoPending = new Map();
const _weatherCache = new Map();
const _weatherPending = new Map();

const getWeatherInfo = (code) => {
  if (code === 0) return { icon: "☀️" };
  if (code <= 2) return { icon: "🌤️" };
  if (code === 3) return { icon: "☁️" };
  if (code <= 48) return { icon: "🌫️" };
  if (code <= 55) return { icon: "🌦️" };
  if (code <= 65) return { icon: "🌧️" };
  if (code <= 77) return { icon: "❄️" };
  if (code <= 82) return { icon: "🌦️" };
  if (code <= 86) return { icon: "🌨️" };
  return { icon: "⛈️" };
};

async function geocode(locationName) {
  if (_geoCache.has(locationName)) return _geoCache.get(locationName);
  if (_geoPending.has(locationName)) return _geoPending.get(locationName);
  const promise = (async () => {
    try {
      const res = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
          locationName,
        )}&count=1&language=ja&format=json`,
      );
      const data = await res.json();
      if (data.results && data.results.length > 0) {
        const coords = {
          lat: data.results[0].latitude,
          lng: data.results[0].longitude,
        };
        _geoCache.set(locationName, coords);
        return coords;
      }
    } catch {
      // Geocoding failures are handled by returning null.
    }
    return null;
  })().finally(() => _geoPending.delete(locationName));
  _geoPending.set(locationName, promise);
  return promise;
}

// YYYY-MM-DD 文字列を加算して返す
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
}

function todayStr() {
  return new Date().toISOString().split("T")[0];
}

// daily レスポンスをパースして { [date]: {date, icon, max, min} } に変換
function parseDailyResponse(data, filterStart, filterEnd) {
  if (!data.daily) return {};
  const { time, temperature_2m_max, temperature_2m_min } = data.daily;
  const codes = data.daily.weather_code || data.daily.weathercode || [];
  const byDate = {};
  (time || []).forEach((date, i) => {
    if (filterStart && date < filterStart) return;
    if (filterEnd && date > filterEnd) return;
    byDate[date] = {
      date,
      icon: getWeatherInfo(codes[i] ?? 0).icon,
      max: temperature_2m_max[i],
      min: temperature_2m_min[i],
    };
  });
  return byDate;
}

async function fetchWeather(lat, lng, startDate, endDate) {
  const cacheKey = `${lat},${lng},${startDate},${endDate}`;
  if (_weatherCache.has(cacheKey)) return _weatherCache.get(cacheKey);
  if (_weatherPending.has(cacheKey)) return _weatherPending.get(cacheKey);
  const promise = (async () => {
    try {
      const today = todayStr();
      // forecast API(past_days付き)でカバーできる範囲: 今日-7日 〜 今日+16日
      const recentFrom = addDays(today, -7);
      const forecastMax = addDays(today, 16);
      // archive API は今日-8日以前（ERA5は数日分遅延あり）
      const archiveCutoff = addDays(today, -8);

      let byDate = {};

      // 区間A: startDate 〜 min(endDate, archiveCutoff) → Archive API
      const archiveEnd = endDate < archiveCutoff ? endDate : archiveCutoff;
      if (startDate <= archiveEnd) {
        const params = new URLSearchParams({
          latitude: lat,
          longitude: lng,
          daily: "weather_code,temperature_2m_max,temperature_2m_min",
          start_date: startDate,
          end_date: archiveEnd,
          timezone: "Asia/Tokyo",
        });
        const res = await fetch(
          `https://archive-api.open-meteo.com/v1/archive?${params}`,
        );
        const data = await res.json();
        Object.assign(byDate, parseDailyResponse(data, startDate, archiveEnd));
      }

      // 区間B: max(startDate, recentFrom) 〜 min(endDate, forecastMax)
      //        → Forecast API + past_days=7（過去7日 + 予報16日を一括取得してフィルタ）
      const recentStart = startDate > recentFrom ? startDate : recentFrom;
      const recentEnd = endDate < forecastMax ? endDate : forecastMax;
      if (recentStart <= recentEnd) {
        const params = new URLSearchParams({
          latitude: lat,
          longitude: lng,
          daily: "weather_code,temperature_2m_max,temperature_2m_min",
          past_days: "7",
          forecast_days: "16",
          timezone: "Asia/Tokyo",
        });
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?${params}`,
        );
        const data = await res.json();
        Object.assign(byDate, parseDailyResponse(data, recentStart, recentEnd));
      }

      if (Object.keys(byDate).length === 0) return null;
      _weatherCache.set(cacheKey, byDate);
      return byDate;
    } catch {
      // Weather API failures are handled by returning null.
    }
    return null;
  })().finally(() => _weatherPending.delete(cacheKey));
  _weatherPending.set(cacheKey, promise);
  return promise;
}

export default {
  name: "WeatherForecast",
  props: {
    location: { type: String, default: "" },
    startDate: { type: String, default: "" },
    endDate: { type: String, default: "" },
    date: { type: String, default: "" }, // バッジモード用: 表示する特定日付
    mode: { type: String, default: "strip" }, // 'strip' | 'badge'
    maxDays: { type: Number, default: null }, // ストリップモード: 最大表示日数
  },
  setup(props) {
    const loading = ref(false);
    const hasError = ref(false);
    const weatherByDate = ref({});

    const days = computed(() => {
      const sorted = Object.values(weatherByDate.value).sort((a, b) =>
        a.date.localeCompare(b.date),
      );
      return props.maxDays ? sorted.slice(0, props.maxDays) : sorted;
    });

    const singleDay = computed(() => {
      if (!props.date || !weatherByDate.value[props.date]) return null;
      return weatherByDate.value[props.date];
    });

    const formatShortDate = (dateStr) => {
      const d = new Date(dateStr + "T00:00:00");
      const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
      return `${d.getMonth() + 1}/${d.getDate()}(${weekdays[d.getDay()]})`;
    };

    const isFarFuture = computed(() => {
      const checkDate =
        props.mode === "badge" ? props.date : props.startDate.split("T")[0];
      if (!checkDate) return false;
      // forecast APIの上限と合わせて today+16日より先は表示不可
      return checkDate > addDays(todayStr(), 16);
    });

    const loadWeather = async () => {
      if (!props.location || !props.startDate) return;
      // 予報範囲外（16日以上先）なら取得しない
      if (isFarFuture.value) return;
      const start = props.startDate.split("T")[0];
      const end = (props.endDate || props.startDate).split("T")[0];
      loading.value = true;
      hasError.value = false;
      try {
        const coords = await geocode(props.location);
        if (!coords) {
          hasError.value = true;
          return;
        }
        const data = await fetchWeather(coords.lat, coords.lng, start, end);
        if (data) weatherByDate.value = data;
        else hasError.value = true;
      } catch {
        hasError.value = true;
      } finally {
        loading.value = false;
      }
    };

    watch(() => [props.location, props.startDate, props.endDate], loadWeather, {
      immediate: true,
    });

    return { loading, hasError, days, singleDay, formatShortDate, isFarFuture };
  },
};
</script>

<style scoped>
/* ストリップスタイル（濃い背景のヘッダー向け） */
.weather-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

.weather-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  min-width: 64px;
  text-align: center;
}

.wd-date {
  font-size: 0.6875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
}

.wd-icon {
  font-size: 1.375rem;
  line-height: 1;
}

.wd-temp {
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.wd-sep {
  color: rgba(255, 255, 255, 0.5);
  margin: 0 1px;
}

/* バッジスタイル（明るい背景のデイヘッダー向け） */
.weather-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  background: var(--color-background);
  padding: 0.25rem 0.625rem;
  border-radius: 99px;
  border: 1px solid var(--color-border-light);
  font-weight: 500;
  white-space: nowrap;
}

/* 予報範囲外メッセージ */
.weather-far-future {
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.75);
  letter-spacing: 0.01em;
}

/* ローディングドット */
.weather-loading {
  display: flex;
  gap: 4px;
  align-items: center;
  margin-top: 0.75rem;
}

.weather-loading-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  animation: weather-blink 1.2s infinite;
}
.weather-loading-dot:nth-child(2) {
  animation-delay: 0.2s;
}
.weather-loading-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes weather-blink {
  0%,
  80%,
  100% {
    opacity: 0.2;
  }
  40% {
    opacity: 1;
  }
}
</style>
